// @flow

import AsyncRequest from '../classes/AsyncRequest';
import ProductGenerator, { isSimpleProduct } from './abstract/ProductGenerator';
import Notifications from '../constants/Notifications';
import RecentlyVisitedProductsCollection from '../classes/ProductFeed/RecentlyVisitedProductsCollection';
import type { InsiderEvent, InsiderProduct } from '../types/UseInsider';
import { mobileCheck } from '../classes/Helpers';

interface ProductFeedInformation {
  products: Array<Object>;
  dataLayer: { productImpressions: { feed: Array<Object> } };
  dataLayerList: string;
}

export default class ProductFeed extends ProductGenerator {
  #ids: string[];
  allowOriginalPrices: boolean = false;
  #insiderHandler: function;
  #insiderCampaignIds: number[] = [];
  #insiderVariationId: number | null = null;
  #insiderProducts: Object | null = null;
  #extraProducts: Object | null = null;
  #location: string | null = null;

  /**
   * Instantiate the component
   */
  bind(el: HTMLElement): void {
    super.bind(el);

    if (!this.el.dataset.ids) {
      if (!this.el.dataset.fetch) {
        throw new Error('A list of ids or an alternative way of fetching is required');
      }

      switch (this.el.dataset.fetch) {
        case 'recently-visited-products':
          this.#ids = new RecentlyVisitedProductsCollection().fetch();
          if (!this.#ids.length) {
            this.notify(Notifications.EMPTY_IDS_FROM_FETCH);
          }
          break;
        default:
          this.#ids = [];
      }
    } else {
      try {
        this.#ids = JSON.parse(this.el.dataset.ids);
      } catch {
        this.#ids = [];
      }
    }

    this.dataLayerList = this.el.dataset.dataLayerList;

    this.#insiderHandler = this.onInsiderCampaign.bind(this);

    if (this.el.dataset.extraProducts) {
      this.#extraProducts = JSON.parse(this.el.dataset.extraProducts);
    }

    const location = this.el.dataset.location;
    if (location) {
      this.#location = location;
    }

    if (this.el.dataset.fetch === 'use-insider-products' && this.el.dataset.campaignIds) {
      try {
        this.#insiderCampaignIds = JSON.parse(this.el.dataset.campaignIds).map((campaignId: string) => +campaignId);
      } catch {
      }

      window.addEventListener('insiderCampaign', this.#insiderHandler);
    } else {
      this.buildProducts();
    }
  }

  /**
   * Build new products
   */
  async buildProducts(): void {
    try {
      const feed = await this.fetchFeed();
      this.createNewProducts(feed.products, feed.dataLayer.productImpressions.feed);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Fetch the products from the API
   */
  async fetchFeed(): Promise<ProductFeedInformation> {
    try {
      if (!this.#ids || !Array.isArray(this.#ids) || !this.#ids.length) {
        throw new Error('The passed ids should be inside an array and we need at least one of them');
      }
    } catch (error) {
      throw new Error(error);
    }

    const parameters = {
      dataLayerList: this.dataLayerList
    };

    if (typeof this.#ids[0] === 'string' && (this.#ids[0].startsWith('MP') || this.#ids[0].startsWith('OP'))) {
      if (this.#ids[0].startsWith('MP')) {
        parameters.product_type = 'simple-product';
      }

      if (this.#extraProducts && typeof this.#extraProducts[0] === 'string' && (this.#extraProducts[0].startsWith('MP') || this.#extraProducts[0].startsWith('OP'))) {
        parameters.product_skus = [...new Set(this.#extraProducts.concat(this.#ids))];
      } else {
        parameters.product_skus = this.#ids;
      }
    } else {
      parameters.product_ids = this.#ids;
    }

    const { data } = await AsyncRequest.post('', parameters, '/product-feed');

    if (!data.products || !data.dataLayer || !data.dataLayer.productImpressions || !data.dataLayer.productImpressions.feed) {
      throw new Error('No products founds for the given ids');
    }
    return data;
  }

  /**
   *
   */
  onInsiderCampaign(data: InsiderEvent): void {
    if (!this.#insiderCampaignIds.includes(data.detail.campaignId)) return;
    this.#ids = data.detail.products.map((product: InsiderProduct) => this.el.dataset.isSimple === 'true' ? product.simpleSku : product.visibleSku);
    this.#insiderProducts = data.detail.products;
    this.#insiderVariationId = data.detail.variationId;
    this.buildProducts();
  }

  createNewProducts(products: Array<Object>, dataLayerProducts: Array<Object>): void {
    const insider = !!this.#insiderVariationId;

    if (this.#insiderProducts) {
      products.map((product: Object) => {
        if (this.el.dataset.isSimple === 'true') {
          product.url = this.#insiderProducts.filter((insiderProduct: Object) => insiderProduct.simpleSku === product.sku)[0]?.url ?? `${window.location.origin}/${product.slug}?=${product.sku}`;
          product.simpleSku = this.#insiderProducts.filter((insiderProduct: Object) => insiderProduct.visible === product.sku)[0]?.simpleSku ?? `${product.sku}`;
        } else {
          product.url = this.#insiderProducts.filter((insiderProduct: Object) => insiderProduct.visibleSku === product.sku)[0]?.url ?? `${window.location.origin}/${product.slug}?=${product.sku}`;
          product.simpleSku = this.#insiderProducts.filter((insiderProduct: Object) => insiderProduct.visibleSku === product.sku)[0]?.simpleSku ?? `${product.sku}`;
        }
        return product;
      });
    }

    super.createNewProducts(products, dataLayerProducts, this.#insiderVariationId, this.#location, this.#extraProducts);

    if (!insider) return;
    this.addInsiderInformation(products);
  }

  addInsiderInformation(products: Array<Object>): void {
    this.el.closest('[ref="slider-wrapper"]')?.classList.add(`ins-preview-wrapper-${this.#insiderVariationId}`);
    this.el.classList.add(mobileCheck() ? 'ins-mobile-web-smart-recommender-body' : 'ins-web-smart-recommender-body');
    this.el.dataset.recommendedItems = JSON.stringify(isSimpleProduct(products[0])
      ? this.#ids
      : products.map((product: Object) => {
        return product.variants.length ? product.variants[0].sku : '';
      }).filter(Boolean)
    );
  }

  /**
   *
   */
  destroy(): void {
    super.destroy();
    window.removeEventListener('insiderCampaign', this.#insiderHandler);
  }
}
